<template>
  <div>
    <h2 class="header-required">Contributors</h2>

    <page-snippet name="contributors" />

    <b-table-simple>
      <b-tbody>
        <b-tr v-for="(contributors, role) in contributorsByRole" :key="role">
          <b-td class="text-nowrap">{{ role }}</b-td>
          <b-td>
            <draggable 
              tag="ul" 
              class="inline-comma-list font-weight-bold" 
              :value="contributors" 
              @input="contributorDragged"
            >
              <li v-for="c in contributors" :key="c.originalIndex" class="draggable">
                <b-icon icon="grip-vertical" />
                {{ c.NamePart1 }} {{ c.NamePart2 }} {{ c.NamePart3 }}
                <strong
                  class="text-danger m-0 ml-2" 
                  v-if="c.ProtectionStatus && c.ProtectionStatus.BlockForDelivery"
                >
                  ({{ c.ProtectionStatus.PortalDescription | trim }})
                </strong> 
                <a class="text-danger" 
                    href="#" 
                    @click.prevent="deleteItem(c)"
                    v-b-tooltip.hover
                    :title="$t('Delete')"
                >
                  <b-icon icon="x" font-scale="2" />
                </a>
                <a href="#"
                  @click.prevent="doCopyToTracks(c)" 
                  v-if="copyToTracks"
                  v-b-tooltip.hover
                  :title="$t('Copy to all tracks')"
                >
                  <b-icon icon="chevron-double-right" shift-v="1" font-scale="1.2" />
                </a>
              </li>
            </draggable>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>

    <b-table v-if="false"
        ref="contributorTable"
        small 
        :fields="contributorTableFields"
        :items="sortedContributors"
      >

      <template v-slot:cell(DisplayPriority)="data">
        {{ 100 - data.item.DisplayPriority }}
      </template>

      <template v-slot:cell(Name)="data">
        {{ data.item.NamePart1 }} {{ data.item.NamePart2 }} {{ data.item.NamePart3 }}
      </template>

      <template v-slot:cell(actions)="data">
        <a href="#" @click.prevent="deleteItem(data)"><b-icon-x /></a>
        <a href="#"
            @click.prevent="$emit('copyToTracks', 'Contributor', null, {...data.item})" 
            v-if="copyToTracks"
            v-b-tooltip.hover
            title="Copy to all tracks"><b-icon-chevron-double-right /></a>
        <a href="#" @click.prevent="moveContributor(data.item, -1)"><b-icon-chevron-up /></a>
        <a href="#" @click.prevent="moveContributor(data.item, 1)"><b-icon-chevron-down /></a>

      </template>
    </b-table>

    <b-modal
      no-close-on-esc no-close-on-backdrop
      v-model="showSearch"
      size="xl"
      :title="$t('Add Contributor')"
      hide-footer
      @shown="contributorSearchVisible = true"
      @hidden="contributorSearchVisible = false"
      >
      <contributor-search
        v-if="showSearch"
        :type="type"
        :provider-id="model.SourceProviderID" 
        :sub-provider-id="model.SourceSubProviderID"
        :role-defs="defs['Contributors.Role']"
        :current-contributors="contributorsAggregated"
        :visible="contributorSearchVisible"
        @done="contributorSearchDone"
        @add="contributorSearchAdd"
      />
    </b-modal>

    <b-modal
      no-close-on-esc no-close-on-backdrop hide-header-close
      v-model="showEditor"
      scrollable
      size="xl"
      id="edit-contributor" 
      title="Contributor"
      hide-footer
      >

      <contributor-create-form
        :defs="defs"
        :role-picker="true"
        @cancel="showEditor = false"
        @create="createDone"
      />
    </b-modal>

    <page-snippet class="mt-2 mb-2" v-if="hasWatchlist" name="watchlist-contributor" />

    <b-button @click="startSearch">{{ $t('Add Contributor') }}</b-button>
    <b-button class="ml-4" v-if="type == 'album'" @click="copyFromTracks">Copy contributors from tracks</b-button>

    <b-alert :show="showValidationErrors.value" v-for="(error, index) in fieldErrors" :key="index" variant="danger" v-html="error" class="mt-4">
    </b-alert>


    <!--
    <div class="mt-3" v-if="copyToTracks"><a href="#" @click.prevent="$emit('copyToTracks', 'Contributors')">Toepassen op alle tracks</a></div>
    -->
  </div>
</template>

<script>
  import { genContributorKey } from '@/functions'
  import draggable from 'vuedraggable'

  export default {
    name: 'ReleaseConceptContributorsV2',
    components: {
      "page-snippet": () => import("@/components/PageSnippet"),
      "contributor-search": () => import("@/components/ContributorSearch"),
      "contributor-create-form": () => import("@/components/ContributorCreateForm"),
      draggable
    },
    data () {
      return {
        showEditor: false,
        showSearch: false,
        roleSortMap: null,
        creatingContributor: false,
        contributorSearchVisible: false,
        contributorTableFields: [
          {
            key: 'DisplayPriority',
            label: 'Display Priority',
          },
          {
            key: 'Name',
            label: 'Name',
          },
          {
            key: 'Role',
            label: 'Role',
          },
          {
            key: 'actions',
            label: 'Actions',
            class: 'link-icons',
          },
        ]
      }
    },
    inject: {
      showValidationErrors: { default: { value: true } }
    },
    props: {
      model: Object,
      validationErrors: Object,
      type: String,
      defs: Object,
      copyToTracks: {
        type: Boolean,
        default: false
      }
    },
    methods: {
      contributorDragged (dragged) {
        let contributors = this.model.Contributors.map(o => ({...o}))
        for (const [index, c] of dragged.entries()) {
          contributors[c.originalIndex].DisplayPriority = 100 - index
        }
        this.resetDisplayPriority(contributors)
      },
      roleSorter (a, b) {
        let key = 'Role'
        if (!this.roleSortMap) {
          this.roleSortMap = {}
          for (const [i, r] of this.defs['Contributors.Role'].Options.entries()) {
            this.roleSortMap[r.JsonValue] = i
          }
        }

        const aKey = this.roleSortMap[a[key]]
        const bKey = this.roleSortMap[b[key]]

        if (aKey === undefined && bKey === undefined) return null
        if (aKey !== undefined && bKey === undefined) return -1
        if (aKey === undefined && bKey !== undefined) return 1

        if (aKey === bKey) return b.DisplayPriority - a.DisplayPriority

        return aKey - bKey
      },
      moveContributor (item, change) {
        let contributors = this.model.Contributors.map(o => ({...o}))
        contributors[item.originalIndex].DisplayPriority -= (change * 1.5)
        this.resetDisplayPriority(contributors)
      },
      resetDisplayPriority (contributors = null) {
        if (!this.model.Contributors) return
        if (!contributors) {
          contributors = this.model.Contributors.map(o => ({...o}))
        }

        contributors = contributors.sort((a, b) => { 
          if (a.Role > b.Role) return 1
          if (a.Role == b.Role) return b.DisplayPriority - a.DisplayPriority
          if (a.Role < b.Role) return -1
        })

        let curRole = null
        let roleIndex = null
        for (const [i, c] of contributors.entries()) {
          if (curRole != c.Role) {
            curRole = c.Role
            roleIndex = i
          }
          c.DisplayPriority = 99 - i + roleIndex
        }

        this.$set(this.model, 'Contributors', contributors)
        this.$emit('change')
      },
      doCopyToTracks(c) {
        this.$emit('copyToTracks', 'Contributor', null, {...this.model.Contributors[c.originalIndex]})
      },
      copyFromTracks () {
        if (!this.model.Contributors)
          this.$set(this.model, 'Contributors', [])

        this.model.Tracks.forEach(track => {
          if (track.UseExisting) {
            track = track.ExistingData
          }

          if (track.Contributors) {
            track.Contributors.forEach(c => {
              if (c.Type == 'Primary') {
                if (!this.model.Contributors.some(c2 => JSON.stringify(c) == JSON.stringify(c2))) {
                  this.model.Contributors.push(JSON.parse(JSON.stringify(c)))
                }
              }
            })
          }
        })
        this.$emit('change')
      },
      startSearch () {
        this.showSearch = true
      },
      contributorSearchDone (action) {
        this.showSearch = false

        if (action === 'cancel') return
        if (action === 'createNew') {
          this.createNew()
          return
        }
      },
      createDone (contributor, roles) {
        this.showEditor = false
        for (const role of roles) {
          this.contributorSearchAdd(contributor, role)
        }
      },
      async contributorSearchAdd(item, role) {
        if (!this.model.Contributors) {
          this.$set(this.model, 'Contributors', [])
        }

        let toAdd = {
          DisplayPriority: 0,
          Role: role,
          Identifiers: {},
        }

        item = JSON.parse(JSON.stringify(item))
        for (const [key, value] of Object.entries(item)) {
          if (!value) continue
          if (![
                  'NamePart1', 
                  'NamePart2', 
                  'NamePart3', 
                  'HasExistingProfile', 
                  'ProfileInformation',
                  'AutoFormatting',
                  'Country',
                  'ProtectionStatus',
                  'Identifiers',
                  'SpotifyData',
                ].includes(key)) continue

          toAdd[key] = value
        }

        toAdd.Type = [
              'Recording Artist',
              'Featuring Artist',
              'ReMixer',
        ].includes(role) ? 'Primary' : 'Default'

        if (!toAdd.NamePart3 && item.NameFull) {
          toAdd.NamePart3 = item.NameFull
        }

        // Check if Contributer/Role combination already exists.
        const existing = this.model.Contributors.find(a => {
          if (a.Role !== role) return false

          if (toAdd.Identifiers.THSO === a.Identifiers.THSO) {
            return true
          }

          if (toAdd.Identifiers.Spotify === a.Identifiers.Spotify) {
            return true
          }
        })

        // If it exists, don't add again.
        if (existing) {
          return
        }

        let contributors = this.model.Contributors.map(o => ({...o}))
        contributors.push(toAdd) 
        this.resetDisplayPriority(contributors)
      },
      createNew () {
        this.showEditor = true
      },
      deleteItem (item) {
        this.model.Contributors.splice(item.originalIndex, true);
        this.$emit('change')
      },
    },
    computed: {
      hasWatchlist () {
        if (!this.model.Contributors) return false
        
        return this.model.Contributors.find(c => {
          return c.ProtectionStatus && c.ProtectionStatus.BlockForDelivery
        })
      },
      sortedContributors () {
        if (!this.model.Contributors) return []
        return this.model.Contributors.map((c, index) => {
          c = {...c}
          c.originalIndex = index
          return c
        }).sort(this.roleSorter)
      },
      contributorsByRole () {
        let roles = {}
        for (const c of this.sortedContributors) {
          if (!roles[c.Role]) {
            roles[c.Role] = []
          }
          roles[c.Role].push(c)
        }

        return roles
      },
      fieldErrors () {
        if (!this.validationErrors || !this.validationErrors['Contributors']) return []
        return this.validationErrors['Contributors']
      },
      contributorsAggregated() {
        if (!this.model.Contributors) return {}
        let out = {}
        for (let c of this.model.Contributors) {
          let key = genContributorKey(c)

          if (!(key in out)) {
            out[key] = {...c}
            out[key].Roles = []
            delete out[key].Role
          }

          out[key].Roles.push(c.Role)
        }

        return out
      }
    },
    mounted () {
      this.resetDisplayPriority()
    }
  }
</script>

<style lang="scss">
  .modal-xl {
    max-width: 80vw !important;
  }

  table td.link-icons {
    a {
      font-weight: normal;
    }

    font-size: 1.65rem;
    
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }
</style>